import { useEffect, useRef, useState } from "react";
import { uploadMedia, publicUrl } from "@/lib/storage";
import { toast } from "sonner";

type Props = {
  value: string | null;
  onChange: (path: string | null) => void;
  prefix: string;
  className?: string;
};

const ImageUploader = ({ value, onChange, prefix, className = "" }: Props) => {
  const ref = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    setPreview(publicUrl(value));
  }, [value]);

  const handle = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }
    setUploading(true);
    try {
      const path = await uploadMedia(file, prefix);
      onChange(path);
      toast.success("Image uploaded");
    } catch (err: any) {
      toast.error(err.message ?? "Upload failed");
    } finally {
      setUploading(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <div className={`flex items-center gap-4 ${className}`}>
      <div className="w-24 h-24 rounded-xl bg-muted overflow-hidden flex items-center justify-center text-xs text-muted-foreground shrink-0">
        {preview ? (
          <img src={preview} alt="" className="w-full h-full object-cover" />
        ) : (
          "No image"
        )}
      </div>
      <div className="flex flex-col gap-2">
        <input ref={ref} type="file" accept="image/*" onChange={handle} className="text-sm" />
        {value && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-xs text-muted-foreground hover:text-destructive text-left"
          >
            Remove image
          </button>
        )}
        {uploading && <span className="text-xs text-primary">Uploading…</span>}
      </div>
    </div>
  );
};

export default ImageUploader;
